import {
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  addMonths,
  subMonths,
  isBefore,
  isAfter,
  startOfMonth,
  endOfMonth,
  addDays,
} from "date-fns";
import * as React from "react";

import { validateInterval, datesInInterval } from "../lib/datetime";
import { getWeeksInMonth } from "../lib/datetime";
import { FirstDayOfWeek, DEFAULT_FIRST_DAY_OF_WEEK } from "../lib/datetime";
import { Text } from "./text";
import { stylex } from "./stylex";
import { cssVariables } from "./css_variables";
import { Hoverable } from "./hoverable";
import { Spacing } from "./spacing";
import { WeekDates } from "./week_dates";
import { Icon } from "./icon";
import { toDay } from "../lib/datetime";
import { DateInterval } from "../offering/date_utils";

const DEFAULT_MONTH_DAY_SIZE = 40;

interface DayPickerProps<TIsRange extends boolean = false> {
  /** Initial date used for navigation */
  startMonth?: Date;
  /** Selected value */
  value?: TIsRange extends true ? DateInterval : Date;
  onChange?: (value: TIsRange extends true ? DateInterval : Date) => void;
  isRange?: TIsRange;
  testID?: string;

  allowBypassBlockedDates?: boolean;
  numberOfMonths?: number;
  orientation?: "vertical" | "horizontal";
  isOutsideRange?: (date: Date) => boolean;
  isBlocked?: (date: Date) => boolean;
}

const VERTICAL_MONTHS_INCREMENT = 2;

function hasBlockedDate(
  interval: DateInterval,
  isBlocked?: (date: Date) => boolean
) {
  if (!isBlocked) return false;

  const dates = datesInInterval(interval, addDays);

  return dates.some(isBlocked);
}

export function DayPicker<TIsRange extends boolean = false>(
  props: DayPickerProps<TIsRange>
) {
  const {
    isRange = false,
    value,
    onChange = () => {},
    testID,
    isOutsideRange,
    isBlocked,
    numberOfMonths = 1,
    orientation = "horizontal",
    allowBypassBlockedDates,
  } = props;
  const [interval, setInterval] = React.useState<DateInterval | null>(
    isRange ? (value as DateInterval) : null
  );
  const [navigationDate, setNavigationDate] = React.useState<Date>(
    value ? (isRange ? (value as any).start : value) : new Date()
  );
  const [noOfMonths, setNoOfMonths] = React.useState(numberOfMonths);

  React.useEffect(() => {
    setInterval(isRange ? (value as DateInterval) : null);
  }, [value, isRange]);

  const handleSelect = React.useCallback(
    (date: Date) => {
      if (isRange) {
        if (value) {
          const val = value as DateInterval;

          // When only start was selected, it means user just selected end
          if (isSameDay(val.start, val.end)) {
            if (isBefore(date, val.start)) {
              onChange({
                start: date,
                end: date,
              } as any);
            } else if (
              !allowBypassBlockedDates &&
              hasBlockedDate({ start: val.start, end: date }, isBlocked)
            ) {
              onChange({
                start: date,
                end: date,
              } as any);
            } else {
              onChange({
                start: val.start,
                end: date,
              } as any);
            }

            // When there is already an interval, reset to start only
          } else {
            onChange({
              start: date,
              end: date,
            } as any);
          }
        } else {
          onChange({
            start: date,
            end: date,
          } as any);
        }
      } else {
        onChange(date as any);
      }
    },
    [onChange, isRange, value, isBlocked, allowBypassBlockedDates]
  );

  const handleHoverIn = React.useCallback(
    (date: Date) => {
      if (value && interval) {
        const val = value as DateInterval;

        if (isSameDay(val.start, val.end)) {
          if (isBefore(date, val.start)) {
            setInterval(isRange ? (value as DateInterval) : null);
          } else if (
            !allowBypassBlockedDates &&
            hasBlockedDate({ start: val.start, end: date }, isBlocked)
          ) {
            setInterval(isRange ? (value as DateInterval) : null);
          } else {
            setInterval({
              start: val.start,
              end: date,
            } as any);
          }
        }
      }
    },
    [interval, isRange, value, isBlocked, allowBypassBlockedDates]
  );

  const handleHoverOut = React.useCallback(() => {
    setInterval(isRange ? (value as DateInterval) : null);
  }, [isRange, value]);

  const handlePressNext = React.useCallback(() => {
    setNavigationDate(addMonths(navigationDate, 1));
  }, [navigationDate]);

  const handlePressPrevious = React.useCallback(() => {
    setNavigationDate(subMonths(navigationDate, 1));
  }, [navigationDate]);

  const handlePressMore = React.useCallback(() => {
    setNoOfMonths(noOfMonths + VERTICAL_MONTHS_INCREMENT);
  }, [noOfMonths]);

  const months = [];

  for (let index = 0; index < noOfMonths; index++) {
    months.push(index);
  }

  return (
    <div data-component="DayPicker" data-testid={testID} style={styles("root")}>
      {orientation === "horizontal" && (
        <div
          data-component="DayPicker__ArrowsWrapper"
          style={styles("arrowsWrapper")}
        >
          <button
            data-testid="previous-month"
            style={styles("arrow")}
            onClick={handlePressPrevious}
          >
            <Icon name="arrow-left" size={18} />
          </button>
          <button
            data-testid="next-month"
            style={styles("arrow")}
            onClick={handlePressNext}
          >
            <Icon name="arrow-right" size={18} />
          </button>
        </div>
      )}
      <div
        data-component="DayPicker__MonthsWrapper"
        style={styles(
          orientation === "horizontal"
            ? "monthsWrapperHorizontal"
            : "monthsWrapperVertical"
        )}
      >
        {months.map((index) => {
          const month = addMonths(navigationDate, index);
          const isLast = index === months.length - 1;

          return (
            <React.Fragment key={month.getMonth()}>
              <div
                data-component="DayPicker__MonthWrapper"
                style={styles("monthWrapper")}
              >
                <Hoverable useWrapper onHoverOut={handleHoverOut}>
                  <Month
                    selected={
                      (value
                        ? isRange
                          ? interval
                          : { start: value, end: value }
                        : undefined) as Interval | undefined
                    }
                    date={month}
                    onSelect={handleSelect}
                    isOutsideRange={isOutsideRange}
                    isBlocked={isBlocked}
                    onHoverIn={handleHoverIn}
                  />
                </Hoverable>
              </div>
              {!isLast && <Spacing width={24} height={48} />}
            </React.Fragment>
          );
        })}
      </div>
      {orientation === "vertical" && (
        <>
          <Spacing height={48} />
          <button style={styles("moreButton")} onClick={handlePressMore}>
            <Icon name="chevron-down" size={32} />
          </button>
        </>
      )}
    </div>
  );
}

interface MonthProps {
  onSelect?: (date: Date) => void;
  /**
   * Date with which we infer the month from
   */
  date?: Date;
  selected?: Interval;
  firstDayOfWeek?: FirstDayOfWeek;
  isOutsideRange?: (date: Date) => boolean;
  isBlocked?: (date: Date) => boolean;
  onHoverIn?: (date: Date) => void;
  onHoverOut?: (date: Date) => void;
}

export const Month = (props: MonthProps) => {
  const {
    date = new Date(),
    firstDayOfWeek = DEFAULT_FIRST_DAY_OF_WEEK,
    onSelect,
    selected,
    isOutsideRange = () => false,
    isBlocked = () => false,
    onHoverIn = () => {},
    onHoverOut = () => {},
  } = props;

  if (selected) validateInterval(selected);

  const som = startOfMonth(date);
  const eom = endOfMonth(date);
  const month = getWeeksInMonth(date, firstDayOfWeek);

  return (
    <div style={styles("monthRoot")}>
      <div style={styles("monthNameWrapper")}>
        <Text align="center">{format(date, "MMMM yyyy")}</Text>
      </div>
      <Spacing height={16} />
      <WeekDates />
      <Spacing height={16} />
      <div>
        {month.weeks.map((week, weekIndex) => (
          <div style={styles("weekContainer")} key={weekIndex}>
            {week.days.map((day, dayIndex) => {
              const isWithinCurrentMonth = isSameMonth(day, date);
              const isSelectedStart =
                selected && isSameDay(day, selected.start);
              const isSelectedEnd = selected && isSameDay(day, selected.end);
              const isSelected = isSelectedStart || isSelectedEnd;
              const isWithinSelection =
                selected && !isSelected
                  ? isWithinInterval(day, selected)
                  : false;

              if (!isWithinCurrentMonth) {
                const hasSelectionNextMonth =
                  selected &&
                  isAfter(day, eom) &&
                  isAfter(selected.end, eom) &&
                  (isSameDay(selected.start, eom) ||
                    isBefore(selected.start, eom));

                const hasSelectionPreviousMonth =
                  selected &&
                  isBefore(day, som) &&
                  isBefore(selected.start, som) &&
                  (isSameDay(selected.end, som) || isAfter(selected.end, som));

                if (hasSelectionNextMonth || hasSelectionPreviousMonth) {
                  return <EmptyDay key={day.getDay()} isWithinSelection />;
                }

                return <EmptyDay key={day.getDay()} />;
              }

              return (
                <Day
                  key={day.getDay()}
                  onSelect={onSelect}
                  onHoverIn={onHoverIn}
                  onHoverOut={onHoverOut}
                  isWithinSelection={isWithinSelection}
                  index={dayIndex}
                  isSelected={isSelected}
                  isSelectedStart={isSelectedStart}
                  isSelectedEnd={isSelectedEnd}
                  date={day}
                  isOutsideRange={isOutsideRange(day)}
                  isBlocked={isBlocked(day)}
                  isToday={isSameDay(new Date(), day)}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

interface DayProps {
  date: Date;
  index: number;
  isWithinSelection?: boolean;
  isSelected?: boolean;
  isSelectedStart?: boolean;
  isSelectedEnd?: boolean;
  isToday?: boolean;
  onSelect?: (date: Date) => void;
  isOutsideRange?: boolean;
  isBlocked?: boolean;
  onHoverIn?: (date: Date) => void;
  onHoverOut?: (date: Date) => void;
}

const Day = (props: DayProps) => {
  const {
    isWithinSelection,
    isSelected,
    date,
    onSelect = () => {},
    isOutsideRange,
    isBlocked,
    isSelectedStart,
    isSelectedEnd,
    isToday,
    onHoverIn = () => {},
    onHoverOut = () => {},
  } = props;

  if (isOutsideRange || isBlocked) {
    return (
      <div style={styles("dayRoot", "disabled")}>
        <div style={styles("dayWrapper")}>
          <Text decoration="lineThrough" color="muted">
            {format(date, "d")}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={styles("dayRoot", isWithinSelection && "withinSelection")}>
      <Hoverable
        onHoverIn={() => onHoverIn(date)}
        onHoverOut={() => onHoverOut(date)}
      >
        {(isHovered) => (
          <button
            data-testid={`day`}
            data-date={`day-${toDay(date)}`}
            style={styles("dayButton")}
            onClick={() => onSelect(date)}
          >
            {isSelectedStart && !isSelectedEnd && (
              <div style={styles("selectedEdge", "selectedStart")} />
            )}
            {isSelectedEnd && !isSelectedStart && (
              <div style={styles("selectedEdge", "selectedEnd")} />
            )}
            <div
              style={styles(
                "dayWrapper",
                isHovered && "hover",
                isSelected && "selected"
              )}
            >
              <Text color={isSelected ? "white" : "default"}>
                {format(date, "d")}
              </Text>
              {isToday && <div style={styles("todayMarker")} />}
            </div>
          </button>
        )}
      </Hoverable>
    </div>
  );
};

interface EmptyDayProps {
  isWithinSelection?: boolean;
}

function EmptyDay(props: EmptyDayProps) {
  const { isWithinSelection } = props;

  return (
    <div style={styles("dayRoot", isWithinSelection && "withinSelection")} />
  );
}

const styles = stylex.create({
  root: {
    position: "relative",
  },
  container: {
    display: "flex",
    flexDirection: "row",
  },
  arrowsWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    zIndex: 1,
    position: "absolute",
    width: "100%",
    left: 0,
    top: 0,
  },
  monthsWrapperHorizontal: {
    display: "flex",
  },
  monthsWrapperVertical: {
    display: "flex",
    flexDirection: "column",
  },
  monthWrapper: {
    flex: 1,
  },
  arrow: {
    height: 40,
    width: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid`,
    borderColor: cssVariables.borderColorDefault,
    boxSizing: "border-box",
    borderRadius: 999,
    padding: 8,
    background: "none",
    textShadow: "none",
    boxShadow: "none",
    color: cssVariables.textColorDefault,
    backgroundColor: "white",
    margin: 0,
  },
  moreButton: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: `1px solid`,
    borderColor: cssVariables.borderColorDefault,
    boxSizing: "border-box",
    padding: 8,
    background: "none",
    textShadow: "none",
    boxShadow: "none",
    color: cssVariables.textColorDefault,
    backgroundColor: "white",
    margin: 0,
    width: "100%",
  },
  monthNameWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    justifyContent: "center",
    height: 40,
  },
  monthRoot: {
    flex: 1,
    width: "100%",
  },
  weekContainer: {
    display: "flex",
    flexDirection: "row",
  },
  dayRoot: {
    position: "relative",
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 2,
    paddingBottom: 2,
  },
  dayButton: {
    background: "none",
    boxShadow: "none",
    margin: 0,
    border: "none",
    display: "flex",
    boxSizing: "border-box",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    cursor: "pointer",
    width: "100%",
    padding: 0,
  },
  dayWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: 999,
    justifyContent: "center",
    textAlign: "center",
    border: "2px solid transparent",
    height: DEFAULT_MONTH_DAY_SIZE,
    width: DEFAULT_MONTH_DAY_SIZE,
  },
  withinSelection: {
    backgroundColor: cssVariables.backgroundLightPrimary,
  },
  disabled: {
    userSelect: "none",
  },
  todayMarker: {
    position: "absolute",
    boxSizing: "border-box",
    backgroundColor: cssVariables.borderColorDefault,
    bottom: 4,
    width: 4,
    height: 4,
    paddingTop: 4,
    borderRadius: 999,
  },
  selected: {
    backgroundColor: cssVariables.backgroundPrimary,
  },
  selectedEdge: {
    zIndex: -1,
    backgroundColor: cssVariables.backgroundLightPrimary,
    width: "50%",
    position: "absolute",
    right: 0,
    height: "100%",
  },
  selectedStart: {
    right: 0,
  },
  selectedEnd: {
    left: 0,
  },
  hover: {
    border: `2px solid ${cssVariables.backgroundPrimary}`,
  },
});
